// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ThresholdsDataObject.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import constant from 'logic/data/Izenda.ThresholdGridConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import { observable, action, computed } from 'mobx';
import { escape, getOr, get, isNil, isEmpty } from 'lodash/fp';
import ReportPartElementProperties from 'logic/models/Izenda.ReportPartElementProperties';
import FormatDataType from 'logic/models/Izenda.FormatDataType';

@tracker
export default class ThresholdsDataObject {
  @observable possion: number = 0;
  @observable id: string = '';
  @observable label: string = '';
  @observable value: string = '';
  @observable dashStyle: string = '';
  @observable color: string = '';
  @observable thickness: string = '';
  @observable visible: boolean = true;
  @observable
  labelSetting = {
    fontSize: 0,
    fontFamily: '',
    fontStyle: {
      fontBold: '',
      fontItalic: '',
      fontUnderline: ''
    },
    textColor: '',
    alignment: ''
  };

  @observable mappingField: string = '';
  @observable thresholdType: string = 'static';
  @observable fieldMappingAlias: string = '';
  @observable fieldId: string = '';
  @observable functionId: string = '';
  @observable functionName: string = '';
  @observable format = new FormatDataType();
  @observable querySourceId: string = '';
  @observable thresholdFieldElement: {};
  constructor(data) {
    this.populateData(data || {});
  }

  @computed
  get displayName() {
    return this.thresholdFieldElement
      ? this.thresholdFieldElement.field.visible
        ? !isNil(this.functionName) && !isEmpty(this.functionName)
          ? `${this.functionName} (${this.thresholdFieldElement.field.fieldName})`
          : this.thresholdFieldElement.field.fieldName
        : ''
      : null;
  }

  populateData(data) {
    const labelStyle = getOr({}, 'izLabel.style', data);
    this.label = getOr('', 'izLabel.text', data);
    this.value = getOr('', 'izValue', data);
    this.thickness = getOr(constant.DEFAULT_VALUE.WIDTH, 'izWidth', data);
    this.color = getOr(constant.DEFAULT_VALUE.COLOR, 'izThresholdColor', data);
    this.visible = getOr(true, 'izVisibleThresholdsSetting', data);
    this.dashStyle = getOr(constant.DEFAULT_VALUE.DASH_STYLE, 'izDashStyle', data);
    this.labelSetting = {
      fontSize: labelStyle.fontSize,
      fontFamily: labelStyle.fontFamily,
      fontStyle: {
        fontBold: labelStyle.fontWeight === constant.DEFAULT_VALUE.FONT_WEIGHT,
        fontItalic: labelStyle.fontStyle === constant.DEFAULT_VALUE.FONT_STYLE,
        fontUnderline: labelStyle.textDecoration === constant.DEFAULT_VALUE.TEXT_DECORATION
      },
      textColor: labelStyle.color || constant.DEFAULT_VALUE.COLOR,
      alignment: getOr(constant.DEFAULT_VALUE.ALIGNMENT, 'izLabel.textAlign', data)
    };
    this.fieldMappingAlias = getOr('', 'fieldMappingAlias', data);
    this.thresholdType = getOr('static', 'thresholdType', data);
    this.fieldId = getOr('', 'fieldId', data);
    this.functionId = getOr('', 'functionId', data);
    this.format.update(getOr({}, 'format', data));
    this.functionName = getOr('', 'functionName', data);
    this.querySourceId = getOr('', 'querySourceId', data);
    this.possion = getOr(0, 'possion', data);
    this.fieldName = data.fieldName;
  }

  /**
   * Clear all dynamic value
   */
  @action
  clearDynamicValue(): void {
    this.value = null;
    this.thresholdFieldElement = null;
    this.functionId = null;
    this.fieldId = null;
    this.fieldName = null;
    this.functionId = null;
    this.format.reset();
  }

  /**
   * Update dynamic threshold element
   * @param {object} element
   */
  @action
  updateElement(element: ReportPartElementProperties): void {
    this.thresholdFieldElement = element;
    if (!isEmpty(this.thresholdFieldElement)) {
      this.thresholdFieldElement.properties.setFormat(this.format);
    }
    this.fieldName = getOr('', 'name', element);
  }

  /**
   * Update field name
   * @param {string} element
   */
  @action
  updateFieldName(fieldName: string): void {
    this.fieldName = fieldName;
    this.markDirty();
  }

  @action
  updateThresholdType(value: string): void {
    this.thresholdType = value;
    this.markDirty();
  }

  @action
  updateFieldMappingAlias(value: string): void {
    this.fieldMappingAlias = value;
    this.markDirty();
  }

  /**
   * Update label name of threshold setting
   * @param  {string} value
   */
  @action
  updateLabel(value: string): void {
    this.label = value;
    this.markDirty();
  }

  @action
  updateField(
    value: string,
    fieldId: string,
    functionId: string,
    functionName: string,
    format: any,
    querySourceId: string
  ): void {
    this.value = value;
    this.fieldId = fieldId;
    this.functionId = functionId;
    this.functionName = functionName;
    this.format.update(format);
    if (!isEmpty(this.thresholdFieldElement)) {
      this.thresholdFieldElement.properties.setFormat(this.format);
    }
    this.querySourceId = querySourceId;
    this.markDirty();
  }

  /**
   * Update threshold setting value
   * @param  {string} value
   */
  @action
  updateValue(value: string): void {
    this.value = value;
    this.markDirty();
  }

  /**
   * Update threshold dash style
   * @param  {string} value
   */
  @action
  updateDashStyle(value: string) {
    this.dashStyle = value;
    this.markDirty();
  }

  /**
   * Update threshold color
   * @param  {string} value
   */
  @action
  updateColor(value: string): void {
    this.color = value;
    this.markDirty();
  }

  /**
   * Update threshold thick ness (width)
   * @param  {string} value
   */
  @action
  updateThickness(value: string) {
    this.thickness = value;
    this.markDirty();
  }

  /**
   * Update threshold thick setting visible
   * @param  {string} value
   */
  @action
  updateVisible(value: boolean) {
    this.visible = value;
    this.markDirty();
  }

  /**
   * Export to chart config setting
   * @return {object} threshold chart setting
   */
  exportToChartConfig(): any {
    const labelSettings = this.labelSetting || {};
    const fontStyle = labelSettings.fontStyle || {};

    return {
      'izLabel.text': escape(this.label),
      izValue: this.value,
      izDashStyle: this.dashStyle,
      izThresholdColor: this.color,
      izWidth: this.thickness,
      izVisibleThresholdsSetting: this.visible,
      'izLabel.style': {
        fontSize: labelSettings.fontSize,
        color: labelSettings.textColor,
        fontFamily: labelSettings.fontFamily,
        fontStyle: fontStyle.fontItalic ? constant.DEFAULT_VALUE.FONT_STYLE : '',
        fontWeight: fontStyle.fontBold ? constant.DEFAULT_VALUE.FONT_WEIGHT : '',
        textDecoration: fontStyle.fontUnderline ? constant.DEFAULT_VALUE.TEXT_DECORATION : ''
      },
      'izLabel.textAlign': this.labelSetting.alignment,
      izIndex: 5, // the zIndex of the plotLine within chart, the number 5 make it above the other(series, etc )
      fieldMappingAlias: this.fieldMappingAlias,
      thresholdType: this.thresholdType,
      fieldId: this.fieldId,
      format: this.format,
      querySourceId: this.querySourceId,
      functionId: this.functionId,
      functionName: this.functionName,
      possion: this.possion,
      fieldName: this.fieldName
    };
  }

  /**
   * Update label settings of text label name
   * @param  {object} value
   */
  @action
  updateLabelSetting(value: any): void {
    value = value || {};

    this.labelSetting.textColor = value.textColor;
    this.labelSetting.fontSize = value.fontSize;
    this.labelSetting.fontFamily = value.fontFamily;
    this.labelSetting.fontStyle = value.fontStyle;
    this.labelSetting.alignment = value.alignment;

    this.markDirty();
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ThresholdsDataObject.js